import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = ["What services do you offer?", "How long does a website take?", "How do I get started?"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const sidRef = useRef(null);

  if (!sidRef.current) {
    let id = localStorage.getItem("gs_chat_sid");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("gs_chat_sid", id);
    }
    sidRef.current = id;
  }

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") break;
          try {
            const parsed = JSON.parse(data);
            const chunk = parsed.delta || parsed.error || "";
            if (chunk) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "The assistant is unavailable right now — please call (507) 841-1801.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed right-4 bottom-24 z-50 flex h-[480px] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0B1026]/95 shadow-2xl shadow-blue-600/20 backdrop-blur-xl sm:right-6"
        >
          <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600">
              <Sparkles className="h-4.5 w-4.5 text-white" />
            </span>
            <div className="flex-1">
              <p className="font-display text-sm font-bold text-white">Nova</p>
              <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
                G-Studio AI Assistant
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-btn"
              className="rounded-lg p-1.5 text-slate-400 transition-colors hover:text-white"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/5 px-3.5 py-2.5 text-sm leading-relaxed text-slate-200">
                  Hi! I'm Nova, the G-Studio assistant. Ask me anything about our websites — or call (507) 841-1801
                  to book a free intro call.
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`}
                      className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-blue-400 hover:text-blue-300"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${i}`}
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto rounded-br-sm bg-blue-600 text-white"
                    : "rounded-tl-sm bg-white/5 text-slate-200"
                }`}
              >
                {m.content || (busy && i === messages.length - 1 ? (
                  <span className="flex gap-1 py-1">
                    <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-slate-400" />
                    <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-slate-400" style={{ animationDelay: "0.2s" }} />
                    <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-slate-400" style={{ animationDelay: "0.4s" }} />
                  </span>
                ) : null)}
              </div>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex gap-2 border-t border-white/10 p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your website…"
              data-testid="chat-input"
              className="flex-1 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-blue-400"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="chat-send-btn"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-500 disabled:opacity-40"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-btn"
        className="fixed right-4 bottom-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg shadow-blue-600/40 transition-transform hover:scale-105 sm:right-6"
        aria-label="Open chat assistant"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </>
  );
}
